import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Award, TrendingUp, DollarSign, Trash2, ChevronRight } from "lucide-react";

const STAGES = [
  { key: "lead", label: "Lead", color: "bg-slate-100 text-slate-700" },
  { key: "qualified", label: "Qualificato", color: "bg-blue-100 text-blue-700" },
  { key: "proposal_sent", label: "Proposta inviata", color: "bg-purple-100 text-purple-700" },
  { key: "negotiation", label: "Negoziazione", color: "bg-amber-100 text-amber-700" },
  { key: "signed", label: "Firmato", color: "bg-emerald-100 text-emerald-700" },
  { key: "active", label: "Attivo", color: "bg-[#1F9D6A]/15 text-[#1F9D6A]" },
  { key: "closed_won", label: "Won", color: "bg-[#1F9D6A] text-white" },
  { key: "closed_lost", label: "Lost", color: "bg-red-100 text-red-700" },
];

export default function AdminAgencyPipeline() {
  const [data, setData] = useState({ by_stage: {}, all: [] });
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", estimated_value: "", win_probability: 30, sales_stage_notes: "", next_action: "", next_action_date: "" });

  const load = async () => {
    const [eng, sum, c] = await Promise.all([
      api.get("/agency/engagements"),
      api.get("/agency/engagements/summary"),
      api.get("/admin/companies"),
    ]);
    setData(eng.data);
    setSummary(sum.data);
    setCompanies(c.data?.companies || c.data || []);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const create = async () => {
    if (!form.company_id) { toast.error("Seleziona un'azienda"); return; }
    try {
      await api.post("/agency/engagements", {
        ...form,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        win_probability: parseInt(form.win_probability),
      });
      toast.success("Engagement creato");
      setOpen(false);
      setForm({ company_id: "", estimated_value: "", win_probability: 30, sales_stage_notes: "", next_action: "", next_action_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const moveStage = async (id, stage) => {
    try {
      await api.put(`/agency/engagements/${id}/stage`, { stage });
      load();
    } catch (e) { toast.error("Errore movimento"); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo engagement?")) return;
    await api.delete(`/agency/engagements/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Award size={24} className="text-[#B7791F]" /> Agency Premium Pipeline
          </h1>
          <p className="mt-1 text-slate-600 text-sm">Gestisci la pipeline degli engagement Agency Premium (lead → won).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#B7791F] hover:bg-[#8B5E12] text-white gap-1.5" data-testid="new-engagement-btn">
              <Plus size={14} /> Nuovo engagement
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="engagement-dialog">
            <DialogHeader><DialogTitle>Nuovo Agency Engagement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Azienda *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger data-testid="engagement-company-select"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Valore stimato (€)</Label><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} placeholder="50000" /></div>
                <div><Label>Win % (0-100)</Label><Input type="number" max="100" value={form.win_probability} onChange={(e) => setForm({ ...form, win_probability: e.target.value })} /></div>
              </div>
              <div><Label>Note di vendita</Label><Textarea rows={2} value={form.sales_stage_notes} onChange={(e) => setForm({ ...form, sales_stage_notes: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prossima azione</Label><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} placeholder="Es. Inviare proposta" /></div>
                <div><Label>Data prossima azione</Label><Input type="date" value={form.next_action_date} onChange={(e) => setForm({ ...form, next_action_date: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={create} className="bg-[#B7791F] text-white" data-testid="engagement-create-btn">Crea</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Engagement totali" value={summary.total_engagements} />
          <StatCard label="Valore pipeline" value={`€${summary.total_value.toLocaleString("it-IT")}`} />
          <StatCard label="Valore ponderato" value={`€${summary.total_weighted_value.toLocaleString("it-IT")}`} />
          <StatCard label="Won" value={summary.by_stage?.closed_won?.count || 0} color="text-[#1F9D6A]" />
        </div>
      )}

      {/* Kanban */}
      <div className="overflow-x-auto">
        <div className="flex gap-3 min-w-max pb-4">
          {STAGES.map((s) => {
            const items = data.by_stage?.[s.key] || [];
            return (
              <div key={s.key} className="w-64 shrink-0" data-testid={`kanban-col-${s.key}`}>
                <div className={`px-3 py-2 rounded-t-lg ${s.color} font-semibold text-xs uppercase tracking-wider flex items-center justify-between`}>
                  {s.label}
                  <span className="cxo-tabular">{items.length}</span>
                </div>
                <div className="bg-slate-50/60 rounded-b-lg p-2 min-h-[300px] space-y-2">
                  {items.length === 0 && <p className="text-xs text-slate-400 text-center mt-4">Vuoto</p>}
                  {items.map((e) => <KanbanCard key={e.id} e={e} stages={STAGES} onMove={moveStage} onDelete={remove} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, color = "text-slate-900" }) => (
  <div className="cxo-card p-3">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 font-display text-xl font-bold cxo-tabular ${color}`}>{value}</p>
  </div>
);

const KanbanCard = ({ e, stages, onMove, onDelete }) => {
  const currentIdx = stages.findIndex((s) => s.key === e.stage);
  const nextStage = stages[currentIdx + 1];
  return (
    <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm" data-testid={`engagement-card-${e.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">{e.company_name}</p>
          {e.company_sector && <p className="text-xs text-slate-500 truncate">{e.company_sector}</p>}
        </div>
        <button onClick={() => onDelete(e.id)} className="text-slate-400 hover:text-red-500" aria-label="Elimina">
          <Trash2 size={12} />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
        <div className="text-slate-500 flex items-center gap-1"><DollarSign size={10} />€{(e.estimated_value || 0).toLocaleString("it-IT")}</div>
        <div className="text-right text-[#1F9D6A] font-semibold cxo-tabular flex items-center justify-end gap-1"><TrendingUp size={10} />{e.win_probability}%</div>
      </div>
      {e.next_action && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Prossima azione</p>
          <p className="text-xs text-slate-700 line-clamp-1">{e.next_action}</p>
        </div>
      )}
      {nextStage && (
        <button
          onClick={() => onMove(e.id, nextStage.key)}
          className="mt-2 w-full text-xs px-2 py-1 rounded-md bg-slate-50 hover:bg-[#EAF0FF] text-slate-600 hover:text-[#2D6BFF] inline-flex items-center justify-center gap-1 transition-colors"
          data-testid={`engagement-advance-${e.id}`}
        >
          Avanza a {nextStage.label} <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
};
